import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import FieldError from '@/components/ui/field-error';
import { Search, X, UserPlus, RefreshCw, Check } from 'lucide-react';

/**
 * Buyer details, in place. Picking or creating a customer never leaves the
 * order screen — the old flow made adding a walk-in customer a separate step
 * you had to come back from, which is the single biggest reason order entry
 * felt like a form to survive rather than a page to fill in.
 */
export default function BuyerPanel({
  jobType, setJobType,
  customerSearch, setCustomerSearch,
  selectedCustomer, setSelectedCustomer,
  showNewCustomer, setShowNewCustomer,
  newCustomer, setNewCustomer,
  newCustomerErrors,
  customers, createCustomerInline,
  deliveryAddress, setDeliveryAddress,
  // a booked order's job type is what its items were made to run as, so editing
  // one shows the toggle as a label rather than a choice
  lockJobType = false,
}) {
  const [focused, setFocused] = useState(false);
  const showResults = focused && !selectedCustomer && customerSearch.trim().length > 0;

  return (
    <section className="border border-slate-200 rounded">
      <header className="bg-brand-blue px-3 py-1.5 rounded-t flex items-center justify-between">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-white">Buyer Details</h3>
        <div className="flex gap-1">
          {[['new', 'New'], ['repeat', 'Repeat']].filter(([value]) => !lockJobType || value === jobType).map(([value, label]) => (
            <button
              key={value}
              type="button"
              disabled={lockJobType}
              onClick={() => setJobType(value)}
              className={`px-2 py-0.5 text-[10px] font-medium rounded ${
                jobType === value ? 'bg-white text-brand-blue' : 'text-blue-100 hover:bg-white/10'
              }`}
            >
              {value === 'repeat' && <RefreshCw className="h-2.5 w-2.5 inline mr-0.5" />}
              {label}
            </button>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-[7rem_1fr] items-stretch border-b border-slate-200">
        <div className="flex items-center bg-slate-100 px-3 py-2 text-xs font-medium text-slate-700 border-r border-slate-200">
          Customer<span className="text-brand-gold-deep ml-0.5">*</span>
        </div>
        <div className="px-2 py-1.5 relative min-w-0">
          {selectedCustomer ? (
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-semibold text-slate-800 truncate">
                {selectedCustomer.display_name || selectedCustomer.customer_name}
              </span>
              <span className="text-xs text-slate-400 shrink-0">{selectedCustomer.mobile_number}</span>
              <button
                type="button"
                onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); }}
                className="ml-auto shrink-0 text-slate-400 hover:text-slate-600"
                aria-label="Change customer"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <Input
                value={customerSearch}
                onChange={e => setCustomerSearch(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 150)}
                placeholder="Type a name, firm or phone number"
                className="h-7 text-sm border-0 shadow-none focus-visible:ring-1 px-1"
                data-testid="customer-search-input"
              />
              <Button
                type="button" size="sm" variant="ghost"
                className="h-6 px-2 text-[11px] text-brand-blue shrink-0"
                onClick={() => setShowNewCustomer(v => !v)}
                data-testid="add-new-customer-btn"
              >
                <UserPlus className="h-3 w-3 mr-1" /> New
              </Button>
            </div>
          )}

          {showResults && (
            <div className="absolute left-2 right-2 top-full z-20 mt-0.5 max-h-56 overflow-y-auto rounded border border-slate-200 bg-white shadow-lg divide-y">
              {customers.length === 0 ? (
                <p className="px-3 py-3 text-xs text-slate-400">
                  No customer matches that. Use <span className="font-medium">New</span> to add one here.
                </p>
              ) : customers.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onMouseDown={() => { setSelectedCustomer(c); setFocused(false); }}
                  className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center justify-between gap-2"
                  data-testid={`customer-${c.id}`}
                >
                  <span className="min-w-0">
                    <span className="block text-sm text-slate-700 truncate">
                      {c.display_name || c.customer_name}
                    </span>
                    <span className="block text-[11px] text-slate-400 truncate">{c.mobile_number || '—'}</span>
                  </span>
                  <Badge variant="outline" className="text-[10px] shrink-0">{c.total_orders || 0} orders</Badge>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-[7rem_1fr] items-stretch">
        <div className="flex items-center bg-slate-100 px-3 py-2 text-xs font-medium text-slate-700 border-r border-slate-200">
          Delivery Add.
        </div>
        <div className="px-2 py-1.5">
          <Input
            value={deliveryAddress}
            onChange={e => setDeliveryAddress(e.target.value)}
            placeholder={selectedCustomer?.delivery_address || selectedCustomer?.address || 'Where it is going'}
            className="h-7 text-sm border-0 shadow-none focus-visible:ring-1 px-1"
            data-testid="delivery-address-input"
          />
        </div>
      </div>

      {showNewCustomer && !selectedCustomer && (
        <div className="border-t border-slate-200 bg-slate-50 p-2.5 space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">New Customer</p>
          <div>
            <label className="text-[10px] text-slate-500">Firm Name, Customer Name *</label>
            <Input
              value={newCustomer.display_name || ''}
              onChange={e => setNewCustomer(p => ({ ...p, display_name: e.target.value }))}
              placeholder="Shreeji Packaging, Ramesh Kumar"
              className="h-7 text-xs"
            />
            <p className="text-[10px] text-slate-400">
              Type the firm name, a comma, then the contact person
            </p>
            <FieldError error={newCustomerErrors?.display_name} />
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {[
              ['mobile_number', 'Mobile', '10-digit number'],
              ['email', 'Email', 'Optional'],
            ].map(([key, label, placeholder]) => (
              <div key={key}>
                <label className="text-[10px] text-slate-500">{label}</label>
                <Input
                  value={newCustomer[key] || ''}
                  onChange={e => setNewCustomer(p => ({ ...p, [key]: e.target.value }))}
                  placeholder={placeholder}
                  className="h-7 text-xs"
                />
                <FieldError error={newCustomerErrors?.[key]} />
              </div>
            ))}
          </div>
          <div>
            <label className="text-[10px] text-slate-500">Address</label>
            <Input
              value={newCustomer.address || ''}
              onChange={e => setNewCustomer(p => ({ ...p, address: e.target.value, delivery_address: e.target.value }))}
              placeholder="Full address for delivery"
              className="h-7 text-xs"
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs bg-brand-blue hover:bg-brand-blue-dark text-white"
              onClick={createCustomerInline}>
              <Check className="h-3 w-3 mr-1" /> Save & use
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowNewCustomer(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
